/**
 * PerformancePage
 * Main page for employee performance tracking
 * Shows different views based on user role with comprehensive export
 */

import React, { useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { TrendingUp, Users, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePerformancePermissions, usePerformanceTabs } from '../../hooks';
import { useMyPerformance, useTeamPerformance, useOrganizationPerformance } from '../../hooks/usePerformanceData';
import { MyPerformanceDashboard, TeamPerformanceDashboard, OrgPerformanceDashboard } from '../../components/performance';
import { PeriodSelector } from '../../components/performance/PeriodSelector';
import { PeriodDateSelector, getDefaultPeriod, type SelectedPeriod } from '../../components/performance/PeriodDateSelector';
import { ComprehensiveExportButton } from '../../components/performance/ComprehensiveExportButton';
import { Card } from '../../components/common';
import type { PeriodType, PerformanceQueryParams } from '../../types';
import {
  parsePerformanceUrlState,
  applyPerformanceUrlState,
  type PerformanceUrlState,
  type PerformanceTabId as TabId,
} from '../../utils/performanceUrlState';

export const PerformancePage: React.FC = () => {
  useAuth(); // Auth context is used by usePerformancePermissions
  const permissions = usePerformancePermissions();
  const availableTabs = usePerformanceTabs();

  // The period is the page's single master control, and it lives in the URL together with
  // the tab and the open side panel. Component state was lost whenever the user opened an
  // OKR and came back, and each tab used to keep its own period besides.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => parsePerformanceUrlState(searchParams), [searchParams]);

  // `replace` so that changing a filter does not add a Back step of its own.
  const updateUrlState = useCallback(
    (patch: Partial<PerformanceUrlState>) => {
      setSearchParams((prev) => applyPerformanceUrlState(prev, patch), { replace: true });
    },
    [setSearchParams]
  );

  const activeTab: TabId =
    urlState.tab && availableTabs.some((t) => t.id === urlState.tab)
      ? urlState.tab
      : ((availableTabs[0]?.id as TabId) || 'my-performance');

  const periodType = urlState.periodType;
  const selectedPeriod: SelectedPeriod = useMemo(
    () =>
      urlState.startDate && urlState.endDate
        ? { startDate: urlState.startDate, endDate: urlState.endDate }
        : getDefaultPeriod(periodType),
    [urlState.startDate, urlState.endDate, periodType]
  );

  const handlePeriodTypeChange = useCallback(
    (newPeriodType: PeriodType) => {
      const next = getDefaultPeriod(newPeriodType);
      updateUrlState({ periodType: newPeriodType, startDate: next.startDate, endDate: next.endDate });
    },
    [updateUrlState]
  );

  const handlePeriodChange = useCallback(
    (period: SelectedPeriod) => {
      updateUrlState({ startDate: period.startDate, endDate: period.endDate });
    },
    [updateUrlState]
  );

  const handleTabChange = (tab: TabId) => {
    // The side panel belongs to the Team tab; do not carry it to another tab.
    updateUrlState({ tab, userId: undefined });
  };

  const handleSelectUser = useCallback(
    (userId: string | null) => updateUrlState({ userId: userId ?? undefined }),
    [updateUrlState]
  );

  // A normal push, so the browser's Back returns to this exact URL — period, tab and open
  // panel. `returnTo` does the same for the OKR page's own "← Back" button, which otherwise
  // always goes to the OKR list.
  const openWithReturn = useOpenWithReturn();
  const handleOKRClick = useCallback(
    (okrId: string) => openWithReturn(`/okrs/${okrId}`),
    [openWithReturn]
  );

  const queryParams: PerformanceQueryParams = useMemo(
    () => ({
      periodType,
      startDate: selectedPeriod.startDate,
      endDate: selectedPeriod.endDate,
    }),
    [periodType, selectedPeriod]
  );

  // Fetch data for comprehensive export (only fetch what user has permission for)
  const { data: myPerformance, isLoading: isMyLoading } = useMyPerformance(
    queryParams,
    permissions.canViewOwn
  );

  const { data: teamPerformance, isLoading: isTeamLoading } = useTeamPerformance(
    queryParams,
    permissions.canViewTeam
  );

  const { data: orgPerformance, isLoading: isOrgLoading } = useOrganizationPerformance(
    queryParams,
    permissions.canViewOrg
  );

  const isLoading = isMyLoading || isTeamLoading || isOrgLoading;

  // If user has no performance access
  if (!permissions.canViewOwn) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <TrendingUp className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-semibold text-stone-700 mb-2">
            Performance Tracking Not Available
          </h2>
          <p className="text-stone-500">
            You don't have access to performance tracking features.
            Please contact your administrator.
          </p>
        </Card>
      </div>
    );
  }

  const TAB_ICONS: Record<TabId, React.ReactNode> = {
    'my-performance': <TrendingUp className="w-4 h-4" />,
    'team-performance': <Users className="w-4 h-4" />,
    'organization': <Building2 className="w-4 h-4" />,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header with Export */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Performance</h1>
          <p className="mt-1 text-stone-500">
            Track and analyze performance based on OKR progress
          </p>
        </div>

        {/* Period (master control for every tab) and Export */}
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodSelector
            value={periodType}
            onChange={handlePeriodTypeChange}
            disabled={isLoading}
          />
          <PeriodDateSelector
            periodType={periodType}
            selectedPeriod={selectedPeriod}
            onChange={handlePeriodChange}
            disabled={isLoading}
          />
          {permissions.canExport && (
            <ComprehensiveExportButton
              individualData={myPerformance}
              teamData={teamPerformance}
              organizationData={orgPerformance}
              periodType={periodType}
              userName={myPerformance?.userName}
              isLoading={isLoading}
              variant="primary"
              size="md"
              showDropdown={availableTabs.length > 1}
            />
          )}
        </div>
      </div>

      {/* Tabs (only show if more than one tab available) */}
      {availableTabs.length > 1 && (
        <div className="mb-6 border-b border-stone-200">
          <nav className="flex gap-4" aria-label="Performance tabs">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as TabId)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                )}
              >
                {TAB_ICONS[tab.id as TabId]}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'my-performance' && (
        <MyPerformanceDashboard queryParams={queryParams} onOKRClick={handleOKRClick} />
      )}

      {activeTab === 'team-performance' && (
        <TeamPerformanceDashboard
          queryParams={queryParams}
          selectedUserId={urlState.userId ?? null}
          onSelectUser={handleSelectUser}
          onOKRClick={handleOKRClick}
        />
      )}

      {activeTab === 'organization' && <OrgPerformanceDashboard queryParams={queryParams} />}
    </div>
  );
};

export default PerformancePage;
